import { Formik } from "formik";
import React from "react";
import { ReleaseNoteModel } from "../../types/ReleaseNoteModel";
import {Button, Divider, InputLabel, TextField} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {DateField} from "@mui/x-date-pickers/DateField/DateField";
import InputWithLeftLabel from "../Forms/FormSelect";
import MDEditor from "@uiw/react-md-editor";

export interface Props {
    releaseNote: ReleaseNoteModel;
    onSubmit: (releaseNote: ReleaseNoteModel) => Promise<boolean>;
    resetButtonClickHandler: () => void;
}

const useStyles = makeStyles((theme) => ({
    buttonSection: {
        marginTop: theme.spacing(3),
    },
    buttonGroup: {
        marginTop: theme.spacing(2),
        display: "flex",
        gap: theme.spacing(1),
    },
}));

export default function ReleaseNoteEditor(props: Readonly<Props>) {
    const classes = useStyles();

    return (
            <Formik
                enableReinitialize
                initialValues={props.releaseNote}
                validate={ValidateForm}
                validateOnMount={true}
                onSubmit={async (values : ReleaseNoteModel) => {
                    let result = await props.onSubmit(values);
                    if (result) {
                        props.resetButtonClickHandler();
                    }
                }}
            >
                {({
                    values,
                    errors, 
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isValid,
                    dirty,
                }) => (
                    <form onSubmit={handleSubmit}>
                        <input value={values.id} type={"number"} id={"id"} hidden onChange={handleChange} />
                        <InputWithLeftLabel 
                            label={"Version *"} 
                            inputJsx={
                                <TextField 
                                    id="version" 
                                    fullWidth
                                    value={values.version}
                                    onChange={handleChange}
                                    error={Boolean(errors.version)}
                                    onBlur={handleBlur}
                                    helperText={
                                        (errors.version) ||
                                        "x.y.z"
                                    }
                                    variant="outlined"
                                />
                            } 
                        />
                            
                        <InputWithLeftLabel
                            label={"Title"}
                            inputJsx={
                                <TextField
                                    id="title"
                                    fullWidth
                                    value={values.title}
                                    onChange={handleChange}
                                    error={Boolean(errors.title)}
                                    onBlur={handleBlur}
                                    helperText={
                                        (touched.title && errors.title) ||
                                        getMaxCharactersText(values.title, 256)
                                    }
                                    variant="outlined"
                                />
                            }
                        />
                            
                        <InputWithLeftLabel
                            label={"Release Date *"}
                            inputJsx={
                                <DateField 
                                    id="timestampUtc"
                                    fullWidth
                                    value={values.timestampUtc}
                                    onChange={(e) => handleChange({type: "change", target: {name: "timestampUtc", value: e!}})}
                                    error={Boolean(errors.timestampUtc)}
                                    onBlur={handleBlur}
                                    variant="outlined"
                                    format="dd-MM-yyyy"
                                />
                            }
                        />
                        
                        <InputWithLeftLabel
                            label={"Message *"}
                            inputJsx={
                                <>
                                    <MDEditor
                                        id="message"
                                        value={values.message}
                                        onChange={(e) => handleChange({type: "change", target: {name: "message", value: e}})}
                                        onBlur={(e) => handleBlur({target: {name: "message", value: e}})}
                                    />
                                    <InputLabel
                                        htmlFor="message"
                                        error={touched.message && Boolean(errors.message)}
                                    >
                                        {
                                            (errors.message) ||
                                            getMaxCharactersText(values.message, 2000)
                                        }
                                    </InputLabel>
                                </>
                            }
                        />
                        
                        <div className={classes.buttonSection}>
                            <Divider />
                            <div className={classes.buttonGroup}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={!dirty || !isValid}
                                >
                                    Save
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => {
                                        props.resetButtonClickHandler();
                                    }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </Formik>
    );
}

function ValidateForm(values: ReleaseNoteModel) {
    const errors: any = {};
    
    if (values.title !== undefined && values.title.length > 256) {
        errors.title = "Title may not be longer as 256 characters";
    }

    if (values.message == undefined || values.message === "") {
        errors.message = "Required";
    } else if (values.message.length > 2000) {
        errors.message = "Message may not be longer than 2000 characters";
    }
    
    if (values.version == undefined || values.version === "") {
        errors.version = "Required";
    } else if (!/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/.test(values.version)) {
        errors.version = "Version must be in the format x.y.z";
    }

    return errors;
}

function getMaxCharactersText(
    inputString: string | undefined,
    maxCharacters: number
): string {
    let inputStringLength = inputString?.length ?? 0;
    return `${inputStringLength}/${maxCharacters}`;
}